import React, {
    forwardRef,
    useRef,
    useEffect,
    useState,
    useImperativeHandle,
    useLayoutEffect,
} from "react";

const HandwritingAnimation = forwardRef(function (
    { svgPaths, maskingPath, onComplete },
    ref
) {
    const svgRef = useRef(null);
    const [playFlag, setPlayFlag] = useState(false);
    const timeoutsRef = useRef([]);
    const cleanupsRef = useRef([]);
    const [ready, setReady] = useState(false);

    // 초기 stroke-dash 셋업
    useLayoutEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const paths = svg.querySelectorAll(".stroke");
        paths.forEach((path) => {
            const len = path.getTotalLength();
            path.style.strokeDasharray = len;
            path.style.strokeDashoffset = len;
        });
        setReady(true);
    }, []);

    // 부모에서 .play() 또는 .complete() 호출 가능
    useImperativeHandle(ref, () => ({
        play: () => setPlayFlag(true),
        complete: () => {
            const svg = svgRef.current;
            if (!svg) return;
            // 진행 중인 타임아웃 모두 취소
            timeoutsRef.current.forEach(clearTimeout);
            // 모든 path 즉시 완료 상태로 세팅
            const paths = Array.from(svg.querySelectorAll(".stroke"));
            paths.forEach((path) => {
                const len = path.getTotalLength();
                path.style.transition = "none";
                path.style.strokeDasharray = `${len}`;
                path.style.strokeDashoffset = `0`;
            });
            cleanupsRef.current.forEach((fn) => fn());
            onComplete?.();
            setPlayFlag(false);
        },
    }));

    useEffect(() => {
        if (!playFlag) return;
        const svg = svgRef.current;
        if (!svg) return;

        const paths = Array.from(svg.querySelectorAll(".stroke")).sort(
            (a, b) => Number(a.dataset.order) - Number(b.dataset.order)
        );

        let finishedCount = 0;
        const total = paths.length;

        // transitionend 리스너
        cleanupsRef.current = paths.map((path) => {
            const handler = (e) => {
                if (e.propertyName !== "stroke-dashoffset") return;
                finishedCount += 1;
                if (finishedCount === total) {
                    onComplete?.();
                    setPlayFlag(false);
                }
            };
            path.addEventListener("transitionend", handler);
            return () => path.removeEventListener("transitionend", handler);
        });

        // 초기화 + 순차 애니메이션
        timeoutsRef.current = paths.map((path, i) => {
            const len = path.getTotalLength();
            path.style.strokeDasharray = `${len}`;
            path.style.strokeDashoffset = `${len}`;
            path.style.transition = "none";

            return window.setTimeout(() => {
                path.style.transition = "stroke-dashoffset 0.3s ease-in-out";
                path.style.strokeDashoffset = "0";
            }, i * 300);
        });

        return () => {
            // 언마운트 또는 playFlag 재실행 시 정리
            timeoutsRef.current.forEach(clearTimeout);
            cleanupsRef.current.forEach((fn) => fn());
        };
    }, [playFlag, onComplete]);

    return (
        <svg
            ref={svgRef}
            id="handwriting"
            width="100%"
            height="auto"
            viewBox="0 0 335 153"
            fill="none"
            style={{
                visibility: ready ? "visible" : "hidden",
                overflow: "visible",
            }}
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <filter
                    id="myShadow"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                >
                    <feDropShadow
                        dx="3"
                        dy="3"
                        stdDeviation="2"
                        floodColor="black"
                        floodOpacity="0.8"
                    />
                </filter>

                <mask
                    id="myMask"
                    fill="white"
                    maskUnits="userSpaceOnUse"
                    maskContentUnits="userSpaceOnUse"
                >
                    {maskingPath.map(({ d }, idx) => (
                        <path key={idx} d={d} fill="white" />
                    ))}
                </mask>
            </defs>

            <g filter="url(#myShadow)">
                {svgPaths.map(({ d }, idx) => (
                    <path
                        key={idx}
                        className="stroke"
                        data-order={idx}
                        d={d}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="10"
                        mask="url(#myMask)"
                    />
                ))}
            </g>
        </svg>
    );
});

export default HandwritingAnimation;
